/**
 * Auth hook - convenience wrapper around authStore.
 *
 * NOTE: Does NOT call checkAuth() - that's handled by ProtectedRoute.
 * This prevents duplicate auth checks that cause excessive API calls.
 */
import { useAuthStore } from '@/stores/authStore';

/**
 * Hook for auth state and actions.
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, isPro, login, logout } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <button onClick={login}>Login</button>;
 * }
 * ```
 */
export function useAuth() {
  const store = useAuthStore();

  const isPro = store.user?.tier === 'approved' || store.user?.tier === 'admin';
  const isAdmin = store.user?.tier === 'admin';

  // Derive boolean flags from FSM status for convenience
  const isAuthenticated = store.status === 'authenticated';
  const isLoading = store.status === 'checking';

  return {
    // State
    user: store.user,
    status: store.status,
    isAuthenticated,
    isLoading,
    error: store.error,

    // Computed
    isPro,
    isAdmin,

    // Actions
    login: store.login,
    logout: store.logout,
    checkAuth: store.checkAuth,
    clearError: store.clearError,
  };
}
